import JSONbig from 'json-bigint';
import { z } from '@customerio/hydra-validation';
import type { LogsLogsReply, operations } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';

type LogsQuery = NonNullable<operations['get-v1-environments-environment_id-logs']['parameters']['query']>;

/**
 * Reads every number written with more than 15 characters as the text it was
 * sent with, so an id such as 9007199254740993 keeps its last digit in the
 * tree and in the preview. That includes a long decimal such as
 * 3.141592653589793, which shows quoted and reaches the preview as a string.
 * Shorter numbers stay numbers.
 */
const losslessJson = JSONbig({ storeAsString: true, protoAction: 'ignore', constructorAction: 'ignore' });

/**
 * Only the outer shape is checked; the logs are taken as the API sends them.
 * The trailing `z.custom` declares that shape without checking it further.
 */
const LogsReply = z.looseObject({ logs: z.array(z.unknown()).nullish() }).pipe(z.custom<LogsLogsReply>());

/**
 * The activity logs a query matches, with the payloads read losslessly: event
 * and webhook data often carries ids larger than a JavaScript number holds.
 */
export async function listLogsLosslessly(workspaceId: number, query: LogsQuery): Promise<LogsLogsReply> {
  const text = await hydraUiApi.logs.list({
    params: { path: { environment_id: String(workspaceId) }, query },
    responseType: 'text',
  });
  const parsed: unknown = losslessJson.parse(text);
  return LogsReply.parse(parsed);
}
